import React, { FunctionComponent, useMemo, useRef, useState, useEffect } from 'react'
import { PaymentType } from '@afterpay/types'
import { Heading, Radio, Text, useIsDesignLanguage, DesignLanguage, Skeleton } from '@afterpaytouch/core'
import {
  setSelectedPaymentType,
  useAppDispatch,
  useCheckoutState,
  useConsumerLendingState,
  useExperiment,
  useIsAfterpayFinanceFeeEligible,
  usePBIRejected,
  usePbiPaymentOption,
  usePclPaymentOptions,
  usePaymentOptionsError,
  usePreselectedPaymentType,
  usePaymentTypeSortOrder,
  useSelectedPaymentType,
} from '../../../state'
import { PaymentOptionsStatus } from '../../../model'
import type { PaymentOption } from '../../../model'
import clsx from 'clsx'
import { ConsumerLendingTagline } from '../../ConsumerLendingTagline'
import { ConsumerLendingRadioLabel } from '../../PaymentTypes/PaymentTypesRadioGroup/ConsumerLendingRadioLabel'
import { PayInFourRadioLabel } from '../../PaymentTypes/PaymentTypesRadioGroup/PayInFourRadioLabel'
import { PCLTooltip } from '../../PaymentTypes/PaymentTypesRadioGroup/PCLTooltip'
import { PaymentOptionLabel } from '../../PaymentOptionLabel'
import { useCheckoutSizes } from '../../../utils/responsive'
import { useTranslation } from '../../../utils/convergence'
import paymentTypesStyles from '../../PaymentTypes/PaymentTypesRadioGroup/style.module.css'
import paymentOptionsStyles from '../style.module.css'
import paymentOptionsRadioGroupStyles from '../PaymentOptionsRadioGroup/style.module.css'

export interface PaymentOptionsExpandableRadioGroupProps {
  lock: boolean
  selectedPaymentOptionLocal?: PaymentOption
  setSelectedPaymentOptionLocal: (option: PaymentOption) => void
  expanded?: boolean
}

export const PaymentOptionsExpandableRadioGroup: FunctionComponent<PaymentOptionsExpandableRadioGroupProps> = ({
  lock,
  selectedPaymentOptionLocal,
  setSelectedPaymentOptionLocal,
  expanded = false,
}) => {
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const selectedPaymentType = useSelectedPaymentType()
  const { started } = useCheckoutState()
  const installmentsCount = started?.paymentSchedules?.length ?? 4
  const dispatch = useAppDispatch()
  const { extraSmallTextSize, textSize } = useCheckoutSizes()
  const hasPBIRejected = usePBIRejected()
  const paymentOptionsError = usePaymentOptionsError()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const paymentTypeSortOrder = usePaymentTypeSortOrder()
  const pclPaymentOptions = usePclPaymentOptions()
  const pbiPaymentOption = usePbiPaymentOption()
  const buttonRef = useRef(null)
  const id = 'payInMoreAPR-help-tooltip'
  const [showTooltip, setShowTooltip] = useState(false)
  const [hasClickedOutside, setHasClickedOutside] = useState<boolean>(false)
  const preselectedPaymentType = usePreselectedPaymentType()
  const { paymentOptionsStatus, paymentOptions } = useConsumerLendingState()

  const payInFourHeadingStyles = clsx(paymentTypesStyles.payInFourHeading, hasPBIRejected && paymentTypesStyles.PBIRejectedGray)
  const { active: isDynamicAprLabelActive } = useExperiment('ConsumerLendingEnableDynamicAprLabel')

  const showPCLLoading = expanded && selectedPaymentType === PaymentType.PCL && paymentOptionsStatus < PaymentOptionsStatus.Fetched
  const showPlans = expanded && paymentOptionsStatus >= PaymentOptionsStatus.Fetched && paymentOptions.length > 0

  const isPbiFirst =
    paymentTypeSortOrder.includes(PaymentType.PBI) &&
    paymentTypeSortOrder.includes(PaymentType.PCL) &&
    paymentTypeSortOrder.indexOf(PaymentType.PBI) < paymentTypeSortOrder.indexOf(PaymentType.PCL)

  const sortedPclOptions = useMemo(
    () =>
      isPbiFirst
        ? [...pclPaymentOptions].sort((a, b) => parseFloat(a.interestAmount?.amount ?? '0') - parseFloat(b.interestAmount?.amount ?? '0'))
        : pclPaymentOptions,
    [pclPaymentOptions, isPbiFirst]
  )

  const clickPaymentTypesGroup = (e): void => {
    const element = e.currentTarget as HTMLInputElement
    dispatch(setSelectedPaymentType(element.value as PaymentType))
  }

  const clickPaymentOptionsGroup = (e) => {
    const value = (e.target as HTMLButtonElement).value
    const clickedId = Number(value)

    if (Number.isNaN(clickedId)) {
      if (Object.values(PaymentType).includes(value as PaymentType)) {
        dispatch(setSelectedPaymentType(value as PaymentType))
      }
      return
    }

    const allOptions = pbiPaymentOption ? [...pclPaymentOptions, pbiPaymentOption] : pclPaymentOptions
    const option = allOptions.find((po) => po.id === clickedId)
    if (option) {
      setSelectedPaymentOptionLocal(option)
      dispatch(setSelectedPaymentType(option.paymentType))
    }
  }

  useEffect(() => {
    if (preselectedPaymentType) {
      dispatch(setSelectedPaymentType(preselectedPaymentType))
    }
  }, [preselectedPaymentType])

  const pclRadioSection = (
    <React.Fragment key='pcl-radio-section'>
      <div className={paymentTypesStyles.payInMoreTagline}>
        <ConsumerLendingTagline />
      </div>

      <Radio.Button
        value={PaymentType.PCL}
        defaultChecked={selectedPaymentType === PaymentType.PCL}
        selectedBorder={selectedPaymentType === PaymentType.PCL && !isArcadeDesignLanguage}
        labelPlacement='Top'
        kind='Option'
        labelWidth='Full'
        roundedBorder
        reversed
        disabled={Boolean(paymentOptionsError?.error)}
        label={() => (
          <ConsumerLendingRadioLabel
            buttonRef={buttonRef}
            showTooltip={showTooltip}
            setShowTooltip={setShowTooltip}
            hasClickedOutside={hasClickedOutside}
            setHasClickedOutside={setHasClickedOutside}
            payInMoreAPRAriaDescribedBy={id}
          />
        )}
        id='payment-types-pcl'
        testNameSpace='payment-types'
        name='payment-types'
      />

      {showTooltip && <PCLTooltip tooltipId={id} buttonRef={buttonRef} setHasClickedOutside={setHasClickedOutside} onClose={() => setShowTooltip(false)} />}
    </React.Fragment>
  )

  const pbiRadioSection = (
    <React.Fragment key='pbi-radio-section'>
      <div className={payInFourHeadingStyles}>
        <Heading size={textSize}>
          {t('consumerLending:payInFourTagline', {
            // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
            context: isAFF ? 'aff' : '',
            interval: installmentsCount,
          })}
        </Heading>
      </div>
      <Radio.Button
        value={PaymentType.PBI}
        selectedBorder={selectedPaymentType === PaymentType.PBI && !isArcadeDesignLanguage}
        defaultChecked={selectedPaymentType === PaymentType.PBI}
        labelPlacement='Top'
        kind='Option'
        disabled={hasPBIRejected}
        roundedBorder
        reversed
        label={() => <PayInFourRadioLabel />}
        id='payment-types-pbi'
        testNameSpace='payment-types'
      />
    </React.Fragment>
  )

  const subjectToChangeSection = (
    <div className={paymentTypesStyles.payInMoreSubjectToChange}>
      <Text color='Gray40' size={isArcadeDesignLanguage ? 'XXS' : extraSmallTextSize} fontFace='CashSansMono'>
        {t('consumerLending:paymentOptions.subjectToChange', {
          // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
          context: isDynamicAprLabelActive ? 'dynamic' : '',
        })}
      </Text>
    </div>
  )

  const expandedPbiSection =
    pbiPaymentOption !== undefined ? (
      <React.Fragment key='pbi-expanded-section'>
        <div className={paymentOptionsRadioGroupStyles.pbiHeading}>
          <Heading size={textSize}>
            {t('consumerLending:payInFourTagline', {
              // eslint-disable-next-line @afterpay/i18n-no-dynamic-context
              context: isAFF ? 'aff' : '',
              interval: pbiPaymentOption.installmentsCount,
            })}
          </Heading>
        </div>
        <Radio.Button
          testNameSpace={`payment-option-${pbiPaymentOption.id}`}
          key={`payment-option-${pbiPaymentOption.id}`}
          id={`payment-option-${pbiPaymentOption.id}`}
          value={pbiPaymentOption.id.toString()}
          reversed
          roundedBorder
          selectedBorder={pbiPaymentOption.id === selectedPaymentOptionLocal?.id && !isArcadeDesignLanguage}
          label={() => <PaymentOptionLabel paymentOption={pbiPaymentOption} selected={pbiPaymentOption.id === selectedPaymentOptionLocal?.id} />}
        />
      </React.Fragment>
    ) : null

  const pclSkeletonSection = (
    <React.Fragment key='pcl-skeleton'>
      <div className={paymentTypesStyles.payInFourHeading}>
        <Heading size={textSize}>{t('consumerLending:payInMoreTagline')}</Heading>
      </div>
      <div className={paymentOptionsStyles.inlineLoaderWrapper}>
        <div className={paymentOptionsStyles.skeleton}>
          <Skeleton kind='Default' />
        </div>
        <div className={paymentOptionsStyles.skeleton}>
          <Skeleton kind='Default' />
        </div>
      </div>
      <div className={paymentOptionsStyles.inlineLoaderWrapper}>
        <div className={paymentOptionsStyles.skeleton}>
          <Skeleton kind='Default' />
        </div>
        <div className={paymentOptionsStyles.skeleton}>
          <Skeleton kind='Default' />
        </div>
      </div>
    </React.Fragment>
  )

  const expandedPclSection = (
    <React.Fragment key='pcl-expanded-section'>
      {showPCLLoading &&
        paymentTypeSortOrder.map((type) => {
          if (type === PaymentType.PCL) return pclSkeletonSection
          if (type === PaymentType.PBI) return pbiRadioSection
          return null
        })}
      {showPlans && (
        <>
          <div className={paymentOptionsRadioGroupStyles.pclTagline}>
            <ConsumerLendingTagline />
          </div>
          {sortedPclOptions.map((paymentOption) => {
            const selected = paymentOption.id === selectedPaymentOptionLocal?.id
            return (
              <Radio.Button
                testNameSpace={`payment-option-${paymentOption.id}`}
                key={`payment-option-${paymentOption.id}`}
                id={`payment-option-${paymentOption.id}`}
                value={paymentOption.id.toString()}
                reversed
                roundedBorder
                selectedBorder={selected && !isArcadeDesignLanguage}
                label={() => <PaymentOptionLabel paymentOption={paymentOption} selected={selected} />}
              />
            )
          })}
        </>
      )}
    </React.Fragment>
  )

  if (expanded) {
    return (
      <>
        <Radio.Group
          id='payment-options'
          name='payment-options'
          testNameSpace='payment-options'
          kind='Option'
          labelWidth='Full'
          labelPlacement='Top'
          value={selectedPaymentOptionLocal?.id.toString()}
          disabled={lock}
          onClick={clickPaymentOptionsGroup}
        >
          {paymentTypeSortOrder.map((type) => {
            if (type === PaymentType.PCL) return expandedPclSection
            if (type === PaymentType.PBI) return expandedPbiSection
            return null
          })}
        </Radio.Group>
      </>
    )
  }

  return (
    <>
      <Radio.Group
        id='payment-types'
        name='payment-types'
        value={selectedPaymentType?.toString()}
        testNameSpace='payment-types'
        onClick={clickPaymentTypesGroup}
      >
        {paymentTypeSortOrder.map((type) => {
          if (type === PaymentType.PCL) return pclRadioSection
          if (type === PaymentType.PBI) return pbiRadioSection
          return null
        })}
      </Radio.Group>
      {subjectToChangeSection}
    </>
  )
}
